import React, { useEffect, useState } from "react";
import { Shield, CheckCircle, Settings, Building, Wifi, AlertTriangle, Globe, RefreshCw } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

// Replace with your real spinner
const LoadingSpinner = ({ section }: { section: string }) =>
    <div className="text-gray-200 text-xs py-4 italic text-center">Loading {section}...</div>;

interface NetworkFirewallProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const NetworkFirewall: React.FC<NetworkFirewallProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [networkFirewallInfo, setNetworkFirewallInfo] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    async function fetchData() {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ client_id, organization_id, agent_id, asset_id }),
            };
            const response = await baseapi.get("/asset_networking", { params });
            const raw = response.data?.data?.[0]?.results;

            setNetworkFirewallInfo(
                raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : {}
            );
        } catch (err: any) {
            showError(err.message);
            setNetworkFirewallInfo({});
        }
        setLoading(false);
    }
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client_id, organization_id, agent_id, asset_id]);

    const totalAdapters = networkFirewallInfo?.networkStats?.totalAdapters || 0;
    const connectedAdapters = networkFirewallInfo?.networkStats?.connectedAdapters || 0;
    const dhcpEnabledAdapters = networkFirewallInfo?.networkStats?.dhcpEnabledAdapters || 0;
    const firewallEnabled = networkFirewallInfo?.firewallEnabled;

    return (
        <div className="p-3">
            {loading ? (
                <LoadingSpinner section="network-firewall" />
            ) : (
                <>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-2">
                        <div className="flex items-center">
                            <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                                <Shield className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h2 className="text-sm font-bold text-white leading-tight">Network & Firewall Configuration</h2>
                                <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider mt-0.5">
                                    {totalAdapters} adapters • {connectedAdapters} connected • Firewall: {firewallEnabled ? 'Enabled' : 'Disabled'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => fetchData()}
                            disabled={loading}
                            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                            title="Refresh data"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* Network Overview Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <div className="bg-blue-500/5 rounded-md border border-blue-500/20 p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-blue-400 text-[11px] font-bold uppercase tracking-tight">Total Adapters</p>
                                    <p className="text-white text-lg font-bold">{totalAdapters}</p>
                                </div>
                                <Wifi className="w-5 h-5 text-blue-500/40" />
                            </div>
                            <div className="bg-green-500/5 rounded-md border border-green-500/20 p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-green-300 text-[11px] font-bold uppercase tracking-tight">Connected</p>
                                    <p className="text-white text-lg font-bold">{connectedAdapters}</p>
                                </div>
                                <CheckCircle className="w-5 h-5 text-green-500/40" />
                            </div>
                            <div className="bg-purple-500/5 rounded-md border border-purple-500/20 p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-purple-300 text-[11px] font-bold uppercase tracking-tight">DHCP Enabled</p>
                                    <p className="text-white text-lg font-bold">{dhcpEnabledAdapters}</p>
                                </div>
                                <Settings className="w-5 h-5 text-purple-500/40" />
                            </div>
                            <div className="bg-orange-500/5 rounded-md border border-orange-500/20 p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-orange-300 text-[11px] font-bold uppercase tracking-tight">Firewall Status</p>
                                    <p className={`text-base font-bold ${firewallEnabled ? 'text-green-400' : 'text-red-400'}`}>
                                        {firewallEnabled ? 'ENABLED' : 'DISABLED'}
                                    </p>
                                </div>
                                <Shield className="w-5 h-5 text-orange-500/40" />
                            </div>
                        </div>

                        {/* Domain Information & External Connection (Side by Side) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Domain Information */}
                            {networkFirewallInfo?.domainInfo && (
                                <div className="bg-gray-900/40 rounded-md border border-gray-700 p-3">
                                    <h3 className="text-xs font-bold text-white mb-3 flex items-center uppercase tracking-widest">
                                        <Building className="w-3.5 h-3.5 mr-2 text-orange-500" />
                                        Domain Information
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-gray-800/20 rounded border border-gray-700/50 p-2">
                                            <p className="text-[10px] font-bold text-cyan-500 uppercase mb-0.5">Computer Name</p>
                                            <p className="text-white font-mono text-xs truncate" title={networkFirewallInfo.domainInfo.computerName}>
                                                {networkFirewallInfo.domainInfo.computerName}
                                            </p>
                                        </div>
                                        <div className="bg-gray-800/20 rounded border border-gray-700/50 p-2">
                                            <p className="text-[10px] font-bold text-green-500 uppercase mb-0.5">Domain</p>
                                            <p className="text-white text-xs truncate" title={networkFirewallInfo.domainInfo.domain}>
                                                {networkFirewallInfo.domainInfo.domain}
                                            </p>
                                        </div>
                                        <div className="col-span-2 bg-gray-800/20 rounded border border-gray-700/50 p-2 flex items-center justify-between">
                                            <p className="text-[10px] font-bold text-purple-500 uppercase">Status</p>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${networkFirewallInfo.domainInfo.domainJoined
                                                ? 'text-green-400 border-green-500/20 bg-green-500/5'
                                                : 'text-yellow-400 border-yellow-500/20 bg-yellow-500/5'
                                                }`}>
                                                {networkFirewallInfo.domainInfo.domainJoined ? 'DOMAIN JOINED' : 'WORKGROUP'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* External Connection Info */}
                            {networkFirewallInfo?.externalIP && (
                                <div className="bg-gray-900/40 rounded-md border border-gray-700 p-3">
                                    <h3 className="text-xs font-bold text-white mb-3 flex items-center uppercase tracking-widest">
                                        <Globe className="w-3.5 h-3.5 mr-2 text-orange-500" />
                                        External Connection
                                    </h3>
                                    <div className="grid grid-cols-1 gap-2">
                                        <div className="bg-gray-800/20 rounded border border-gray-700/50 p-2 flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-green-500 uppercase">External IP</span>
                                            <p className="text-white font-mono text-xs font-bold">{networkFirewallInfo.externalIP}</p>
                                        </div>
                                        <div className="bg-gray-800/20 rounded border border-gray-700/50 p-2 flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-blue-400 uppercase">Internet Access</span>
                                            <p className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${networkFirewallInfo.internetAccess ? 'text-green-400 border-green-500/20 bg-green-500/10' : 'text-red-400 border-red-500/20 bg-red-500/10'}`}>
                                                {networkFirewallInfo.internetAccess ? 'AVAILABLE' : 'OFFLINE'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Network Adapters (Converted to Table) */}
                        <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden">
                            <div className="px-3 py-2 bg-gray-800/60 border-b border-gray-700/50 flex items-center justify-between">
                                <h3 className="text-xs font-bold text-white flex items-center uppercase tracking-widest">
                                    <Wifi className="w-3.5 h-3.5 mr-2 text-orange-500" />
                                    Network Adapters ({networkFirewallInfo?.adapters?.length || 0})
                                </h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse min-w-[1000px]">
                                    <thead className="bg-gray-800/80">
                                        <tr>
                                            <th className="px-3 py-2 text-[11px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 w-[25%]">Adapter Name</th>
                                            <th className="px-3 py-2 text-[11px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 w-[15%]">Status / Type</th>
                                            <th className="px-3 py-2 text-[11px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 w-[15%]">IP Address</th>
                                            <th className="px-3 py-2 text-[11px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 w-[15%]">MAC Address</th>
                                            <th className="px-3 py-2 text-[11px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 w-[15%]">Gateway</th>
                                            <th className="px-3 py-2 text-[11px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 w-[15%]">DNS Servers</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800">
                                        {!networkFirewallInfo?.adapters || networkFirewallInfo.adapters.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-3 py-6 text-center text-gray-200 italic text-[11px]">No adapters found.</td>
                                            </tr>
                                        ) : (
                                            networkFirewallInfo.adapters.map((adapter: any, index: number) => (
                                                <tr key={index} className="hover:bg-gray-800/40 transition-colors">
                                                    <td className="px-3 py-2">
                                                        <div className="flex items-center space-x-2">
                                                            <div className={`w-2 h-2 rounded-full shrink-0 ${adapter.isActive ? 'bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.5)]' : 'bg-red-500'}`}></div>
                                                            <span className="text-white font-bold text-[11px]" title={adapter.name}>
                                                                {adapter.name}
                                                            </span>
                                                        </div>
                                                        {adapter.hasConfigurationError && (
                                                            <div className="flex items-center text-yellow-500 font-bold text-[10px] uppercase mt-1 ml-4">
                                                                <AlertTriangle className="w-3 h-3 mr-1" /> Config Error
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex flex-col gap-1 items-start">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border uppercase ${adapter.connectionStatus === 'Connected'
                                                                    ? 'text-green-400 border-green-500/20 bg-green-500/5'
                                                                    : 'text-red-400 border-red-500/20 bg-red-500/5'
                                                                    }`}>
                                                                    {adapter.connectionStatus}
                                                                </span>
                                                                {adapter.dhcpEnabled && (
                                                                    <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded text-[10px] font-bold uppercase" title="DHCP Enabled">DHCP</span>
                                                                )}
                                                            </div>
                                                            <span className="text-[10px] text-gray-200 uppercase tracking-wider">{adapter.type}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <span className={`font-mono text-[11px] ${adapter.ipAddress !== 'None' ? 'text-white' : 'text-gray-200'}`}>
                                                            {adapter.ipAddress !== 'None' ? adapter.ipAddress : '-'}
                                                        </span>
                                                        {adapter.subnetMask !== 'None' && adapter.ipAddress !== 'None' && (
                                                            <span className="text-gray-200 text-[10px] block mt-0.5">Mask: {adapter.subnetMask}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-300 font-mono text-[11px]">
                                                        {adapter.macAddress && adapter.macAddress !== "N/A" ? adapter.macAddress : "-"}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <span className={`font-mono text-[11px] ${adapter.gateway !== 'None' ? 'text-white' : 'text-gray-200'}`}>
                                                            {adapter.gateway !== 'None' ? adapter.gateway : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="text-white font-mono text-[11px] truncate max-w-[150px]" title={adapter.dnsServers}>
                                                            {adapter.dnsServers !== 'None' ? adapter.dnsServers : <span className="text-gray-200">-</span>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Windows Defender Firewall & Profiles */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Windows Defender Firewall */}
                            {networkFirewallInfo?.windowsDefenderFirewall && (
                                <div className="bg-gray-900/40 rounded-md border border-gray-700 p-3">
                                    <h3 className="text-xs font-bold text-white mb-3 flex items-center uppercase tracking-widest">
                                        <Shield className="w-3.5 h-3.5 mr-2 text-orange-500" />
                                        Defender Firewall Status
                                    </h3>
                                    <div className="flex flex-col gap-2">
                                        <div className="bg-gray-800/20 rounded border border-gray-700/50 p-2 flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-orange-500 uppercase">Operational Status</span>
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${networkFirewallInfo.windowsDefenderFirewall.enabled ? 'text-green-400 border-green-500/20 bg-green-500/10' : 'text-red-400 border-red-500/20 bg-red-500/10'}`}>
                                                {networkFirewallInfo.windowsDefenderFirewall.enabled ? 'ACTIVE' : 'INACTIVE'}
                                            </span>
                                        </div>
                                        <div className="bg-gray-800/20 rounded border border-gray-700/50 p-2 flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-blue-400 uppercase">Service Status</span>
                                            <span className="text-white text-[11px] font-bold tracking-wide">{networkFirewallInfo.windowsDefenderFirewall.serviceStatus}</span>
                                        </div>
                                        <div className="bg-gray-800/20 rounded border border-gray-700/50 p-2 flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-purple-400 uppercase">Engine Version</span>
                                            <span className="text-gray-300 font-mono text-[11px]">{networkFirewallInfo.windowsDefenderFirewall.version}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Firewall Profiles */}
                            {networkFirewallInfo?.firewallProfiles && networkFirewallInfo.firewallProfiles.length > 0 && (
                                <div className="bg-gray-900/40 rounded-md border border-gray-700 p-3">
                                    <h3 className="text-xs font-bold text-white mb-3 flex items-center uppercase tracking-widest">
                                        <Shield className="w-3.5 h-3.5 mr-2 text-orange-500" />
                                        Firewall Profiles
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2">
                                        {networkFirewallInfo.firewallProfiles.map((profile: any, index: number) => (
                                            <div key={index} className="bg-gray-800/20 rounded border border-gray-700/50 p-2 flex flex-col justify-between">
                                                <div className="flex items-center justify-between mb-2 border-b border-gray-700/50 pb-1.5">
                                                    <h4 className="text-white font-bold text-[11px] uppercase tracking-wide">{profile.profile}</h4>
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border uppercase ${profile.enabled === 1 ? 'text-green-400 border-green-500/20 bg-green-500/10' : 'text-red-400 border-red-500/20 bg-red-500/10'}`}>
                                                        {profile.enabled === 1 ? 'ON' : 'OFF'}
                                                    </span>
                                                </div>
                                                <div className="space-y-1 text-[11px] font-mono">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-200 uppercase text-[10px]">Inbound</span>
                                                        <span className={`font-bold ${profile.defaultInboundAction === 1 ? 'text-red-400' : 'text-green-400'}`}>
                                                            {profile.defaultInboundAction == null
                                                                ? 'Not Configured'
                                                                : profile.defaultInboundAction === 1
                                                                    ? 'BLOCK'
                                                                    : 'ALLOW'}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-200 uppercase text-[10px]">Outbound</span>
                                                        <span className={`font-bold ${profile.defaultOutboundAction === 1 ? 'text-red-400' : 'text-green-400'}`}>
                                                            {profile.defaultOutboundAction == null
                                                                ? 'Not Configured'
                                                                : profile.defaultOutboundAction === 1
                                                                    ? 'BLOCK'
                                                                    : 'ALLOW'}
                                                        </span>
                                                    </div>
                                                    {/* <div className="flex justify-between items-center border-t border-gray-700/30 pt-1 mt-1">
                                                        <span className="text-gray-200 uppercase text-[10px]">Log Limit</span>
                                                        <span className="text-gray-300">{profile.logMaxSizeKilobytes} KB</span>
                                                    </div> */}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default NetworkFirewall;